export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  maxRetries?: number;
  retryDelay?: number;
}

export type Metadata = Record<string, string>;

export const uploadFileToS3 = async (
  file: File,
  metadata: Metadata,
  presignedUrl: string,
  options: UploadOptions = {},
): Promise<void> => {
  const { onProgress, signal, maxRetries = 3, retryDelay = 1000 } = options;

  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await uploadFileToS3Single(file, presignedUrl, metadata, {
        onProgress,
        signal,
      });
      return;
    } catch (error) {
      lastError = error as Error;

      // Don't retry if the operation was aborted
      if (lastError.message.includes('aborted')) {
        throw lastError;
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Wait before retrying (exponential backoff)
      const delay = retryDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error(
    `Upload failed after ${maxRetries + 1} attempts: ${lastError?.message}`,
  );
};

const uploadFileToS3Single = async (
  file: File,
  presignedUrl: string,
  metadata: Metadata,
  options: Pick<UploadOptions, 'onProgress' | 'signal'> = {},
): Promise<void> => {
  const { onProgress, signal } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress: UploadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          };
          onProgress(progress);
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    // Handle abort
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Set up abort signal
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    // Start the upload
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);

    // Set metadata headers
    Object.entries(metadata).forEach(([key, value]) => {
      xhr.setRequestHeader(`x-amz-meta-${key}`, value);
    });

    xhr.send(file);
  });
};
